// TemplatesFormatPane.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Box, Paper, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Button, Typography, TableContainer, Table, TableHead, TableRow,
  TableCell, TableBody, Snackbar, Alert
} from "@mui/material";
import { FaPlus, FaExternalLinkAlt, FaUpload } from "react-icons/fa";
import axios from "axios";

const LL_AUTH_HEADERS = () => ({
  Authorization: `Bearer ${localStorage.getItem("labour_law_token") || ""}`,
});

export default function TemplatesFormatPane() {
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);
  const [stateCode, setStateCode] = useState("");
  const [domainCode, setDomainCode] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  const [states, setStates] = useState([]);
  const [domains, setDomains] = useState([]);
  

  // API root - set via env in development or default to localhost
  //const API_ROOT = ("http://127.0.0.1:8000")
  const API_ROOT = ("http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com")

  // optional frontend fallback base url to build public url if backend doesn't provide it
  const S3_PUBLIC_BASE = process.env.REACT_APP_S3_PUBLIC_BASE_URL || "";

  useEffect(() => {
    fetchList();
    fetchLookups();
    fetchCategories();
    // eslint-disable-next-line
  }, []);

  const fetchList = async () => {
    try {
      const resp = await axios.post(
        `${API_ROOT}/docmgmt/templates_list/`,
        {}, // empty body
        { headers: LL_AUTH_HEADERS() } // correct place
      );
      setTemplates(resp.data.items || []);
    } catch (err) {
      console.error("list error", err);
      setTemplates([]);
    }
  };


  const fetchLookups = async () => {
    try {
      const [sResp, dResp] = await Promise.all([
        axios.get(`${API_ROOT}/docmgmt/states/`, { headers: LL_AUTH_HEADERS() }),
        axios.get(`${API_ROOT}/docmgmt/domains/`, { headers: LL_AUTH_HEADERS() })
      ]);
      setStates(sResp.data || []);
      setDomains(dResp.data || []);
    } catch (err) {
      console.warn("lookup fetch failed", err);
    }
  };
  const fetchCategories = async () => {
    try {
      const resp = await axios.get(`${API_ROOT}/docmgmt/templates_categories/`, { headers: LL_AUTH_HEADERS() });
      setCategories(resp.data || []);
    } catch (err) {
      console.warn("category fetch failed", err);
    }
  };


  const onFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
  };

const uploadAndIngest = async () => {
  if (!file || !name) {
    setSnack({ open: true, msg: "Name, category and file required", sev: "warning" });
    return;
  }

  try {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name);
    fd.append("category", category)
    fd.append("state_code", stateCode);
    fd.append("domain_code", domainCode);

    await axios.post(
      `${API_ROOT}/docmgmt/templates_ingest/`,
      fd,
      { headers: { "Content-Type": "multipart/form-data", ...LL_AUTH_HEADERS() } }
    );

    setSnack({ open: true, msg: "Template uploaded successfully", sev: "success" });
    setName("");
    setCategory("");
    setStateCode("");
    setDomainCode("");
    setNewCategory("");
    setAddingCategory(false);
    setFile(null);
    fetchList();
  } catch (err) {
    console.error("ingest error", err);
    setSnack({ open: true, msg: "Upload failed", sev: "error" });
  }
};

  //
  // ---------- End upload helpers ----------
  //

  // Add by providing public URL or S3 key manually (no file)
  const addManual = async () => {
    if (!name || !category || (!url && !file)) {
      setSnack({ open: true, msg: "Name, category and URL/file required", sev: "warning" });
      return;
    }
    try {
      const payload = {
        name,
        state_code: stateCode,
        domain_code: domainCode,
      };
      await axios.post(`${API_ROOT}/docmgmt/templates_ingest/`, payload, { headers: LL_AUTH_HEADERS() });
      setSnack({ open: true, msg: "Template added", sev: "success" });
      setName(""); setDescription(""); setUrl(""); setCategory(""); setStateCode(""); setDomainCode("");
      fetchList();
    } catch (err) {
      console.error(err);
      setSnack({ open: true, msg: "Failed to add", sev: "error" });
    }
  };

  const remove = async (id) => {
    try {
      await axios.post(`${API_ROOT}/docmgmt/templates_delete/`, { id }, { headers: LL_AUTH_HEADERS() });
      setSnack({ open: true, msg: "Removed!", sev: "success" });
      setTemplates((t) => t.filter(x => x.id !== id));
    } catch (err) {
      console.error("delete error", err);
      setSnack({ open: true, msg: "Delete failed", sev: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <TextField label="Template Name" size="small" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={category}
                onChange={(e) => {
                  if (e.target.value === "__add_new__") {
                    setAddingCategory(true);
                    setCategory("");
                  } else {
                    setCategory(e.target.value);
                    setAddingCategory(false);
                  }
                }}
              >
                {categories.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}

                <MenuItem value="__add_new__">
                  ➕ Add new category
                </MenuItem>
              </Select>
            </FormControl>
            {addingCategory && (
              <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="New Category"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  autoFocus
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    if (!newCategory.trim()) return;

                    setCategories(prev => [...new Set([...prev, newCategory.trim()])]);
                    setCategory(newCategory.trim());
                    setNewCategory("");
                    setAddingCategory(false);
                  }}
                >
                  Add
                </Button>
              </Box>
            )}


          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <Select label="State" value={stateCode} onChange={(e) => setStateCode(e.target.value)}>
                <MenuItem value="">--</MenuItem>
                {states.map(s => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Domain</InputLabel>
              <Select label="Domain" value={domainCode} onChange={(e) => setDomainCode(e.target.value)}>
                <MenuItem value="">--</MenuItem>
                {domains.map(d => <MenuItem key={d.code} value={d.code}>{d.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

        </Grid>

        {/* ---------- Step 1: File Upload UI fix (id + htmlFor) ---------- */}
        <Grid container spacing={1} sx={{ mt: 2 }} alignItems="center">
          <Grid item>
            {/* input has an id; label htmlFor will open file dialog */}
            <input
              id="template-file-input"
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={onFileChange}
            />
            <label htmlFor="template-file-input" style={{ cursor: "pointer" }}>
              <Button component="span" startIcon={<FaUpload />}>Choose File</Button>
            </label>
          </Grid>

          <Grid item>
            {file ? <Typography>{file.name}</Typography> : <Typography color="text.secondary">No file selected</Typography>}
          </Grid>

          <Grid item>
            <Button onClick={uploadAndIngest}>
              Upload & Ingest
            </Button>
          </Grid>
        </Grid>
        {/* ---------- end file upload UI ---------- */}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell align="right">Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>{t.category}</TableCell>
                  <TableCell>{t.state_code}</TableCell>
                  <TableCell>{t.domain_code}</TableCell>
                  <TableCell align="right">
                    <Button color="error" onClick={() => remove(t.id)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar open={snack.open} onClose={() => setSnack({ ...snack, open: false })} autoHideDuration={2500}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
